// ManageFollowingSheet — "Churches you follow", rebuilt per
// docs/designs/ChurchesYouFollow.png, docs/FIX-SERMON-CLIPS.md §3d.
// Replaces the old inline Profile view (ProfileSheet's view==="following",
// which just rendered SermonNoteChurchesPicker) with a dedicated sheet,
// stacked over Profile like Notifications.
//
// "Find more churches" would ideally push FindChurchesSheet inline, inside
// this sheet's own body, per the app's third-level rule (never a third
// physical sheet). FindChurchesSheet is a large, independently-built sheet
// with its own search/discover/families UI, so reusing it wholesale here
// instead closes this sheet and opens it as its own top-level sheet — the
// same way every other entry point into it already works — rather than
// re-implementing a trimmed inline copy of it. Closing it reopens this
// sheet, so it isn't a dead end.
//
// Props:
//   open, onClose, backSheetRef — handed to <StackedSheet>
//   churches, homeChurchId, followingIds (Set, excludes home), onToggleFollow(churchId)
//   onChangeChurch()  — closes this sheet and opens Profile's existing church picker
//   onOpenChurch(church)

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import StackedSheet from "./StackedSheet";
import FindChurchesSheet from "./FindChurchesSheet";
import { CircleLogoOrInitials, CIRCLE_PASTELS } from "./ExploreTab";

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

export default function ManageFollowingSheet({
  open, onClose, backSheetRef, churches = [], homeChurchId, followingIds, onToggleFollow, onChangeChurch, onOpenChurch,
}) {
  const [stats, setStats] = useState(null); // church_id -> { network_id, network_name }
  const [families, setFamilies] = useState([]);
  const [confirmUnfollow, setConfirmUnfollow] = useState(null);
  const [findMoreOpen, setFindMoreOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    supabase.rpc("church_stats").then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      const m = {};
      for (const row of data) m[row.church_id] = row;
      setStats(m);
    });
    supabase.from("church_networks").select("id, name, short_name, logo_url")
      .then(({ data }) => { if (!cancelled) setFamilies(data || []); });
    return () => { cancelled = true; };
  }, [open]);

  const home = useMemo(() => churches.find(c => c.id === homeChurchId) || null, [churches, homeChurchId]);
  const homeNetworkId = home?.network_id || stats?.[homeChurchId]?.network_id || null;
  const homeFamilyName = useMemo(() => {
    if (!homeNetworkId) return null;
    return families.find(f => f.id === homeNetworkId)?.name || stats?.[homeChurchId]?.network_name || "Your church family";
  }, [homeNetworkId, families, stats, homeChurchId]);

  const familyMembers = useMemo(() => {
    if (!homeNetworkId) return [];
    return churches.filter(c => c.id !== homeChurchId && followingIds?.has(c.id)
      && (c.network_id === homeNetworkId || stats?.[c.id]?.network_id === homeNetworkId));
  }, [churches, homeNetworkId, homeChurchId, followingIds, stats]);

  const otherFollowed = useMemo(() => {
    const famIds = new Set(familyMembers.map(c => c.id));
    return churches.filter(c => c.id !== homeChurchId && followingIds?.has(c.id) && !famIds.has(c.id));
  }, [churches, homeChurchId, followingIds, familyMembers]);

  function confirmedUnfollow() {
    if (confirmUnfollow) onToggleFollow?.(confirmUnfollow.id);
    setConfirmUnfollow(null);
  }

  if (findMoreOpen) {
    return (
      <FindChurchesSheet
        churches={churches}
        homeChurchId={homeChurchId}
        followingIds={followingIds}
        onToggleFollow={onToggleFollow}
        onOpenChurch={(c) => { setFindMoreOpen(false); onOpenChurch?.(c); }}
        onInviteChurch={() => {}}
        onCreateChurch={() => {}}
        onClose={() => setFindMoreOpen(false)}
      />
    );
  }

  return (
    <StackedSheet open={open} onClose={onClose} backSheetRef={backSheetRef} backLabel="Profile" zIndex={420}>
      <div style={S.header}>
        <div style={S.title}>Churches you follow</div>
        <button type="button" aria-label="Close" onClick={onClose} className="shx"/>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "4px 18px max(18px, env(safe-area-inset-bottom))" }}>
        <div style={S.explainer}>
          You'll see sermon clips and Bible notes from every church you follow. Community, plans and events come from your home church.
        </div>

        <div style={S.sectionHead}>Your church</div>
        <div style={S.homeCard}>
          <div style={{ ...S.circle, width: 48, height: 48, borderRadius: 24, fontSize: 15, background: "var(--lavender)", color: "var(--lavender-ink)" }}>
            <CircleLogoOrInitials url={home?.logo_url} name={home?.name}/>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.homeBadge}>⌂ HOME CHURCH</div>
            <div style={S.homeName}>{home?.name || "Your church"}</div>
            <div style={S.homeSub}>Sermons, plans, community and events</div>
          </div>
          <button type="button" onClick={onChangeChurch} style={S.changeLink}>Change</button>
        </div>

        {familyMembers.length > 0 && (
          <>
            <div style={S.sectionHeadRow}>
              <span style={S.sectionHead}>{(homeFamilyName || "Church family").toUpperCase()}</span>
              <span style={S.sectionSub}>Your church family</span>
            </div>
            <div style={S.card}>
              {familyMembers.map((c, i) => (
                <ChurchRow key={c.id} church={c} index={i} last={i === familyMembers.length - 1}
                  onOpen={() => onOpenChurch?.(c)} onToggle={() => setConfirmUnfollow(c)}/>
              ))}
            </div>
          </>
        )}

        {otherFollowed.length > 0 && (
          <>
            <div style={S.sectionHead}>Other churches</div>
            <div style={S.card}>
              {otherFollowed.map((c, i) => (
                <ChurchRow key={c.id} church={c} index={familyMembers.length + i} last={i === otherFollowed.length - 1}
                  onOpen={() => onOpenChurch?.(c)} onToggle={() => setConfirmUnfollow(c)}/>
              ))}
            </div>
          </>
        )}

        <button type="button" onClick={() => setFindMoreOpen(true)} style={S.findMoreCard}>
          <span style={S.findMorePlus}>+</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.findMoreTitle}>Find more churches</div>
            <div style={S.findMoreSub}>Near you, in your church family, or popular</div>
          </div>
          <span style={{ color: "var(--stone)", fontSize: 18, flexShrink: 0 }}>›</span>
        </button>

        <div style={S.footer}>Tap ✓ Following to unfollow.</div>
      </div>

      {confirmUnfollow && <UnfollowConfirm church={confirmUnfollow} onCancel={() => setConfirmUnfollow(null)} onConfirm={confirmedUnfollow}/>}
    </StackedSheet>
  );
}

function ChurchRow({ church, index, last, onOpen, onToggle }) {
  const pastel = CIRCLE_PASTELS[index % CIRCLE_PASTELS.length];
  return (
    <div style={{ ...S.row, borderBottom: last ? "none" : "1px solid var(--border)" }}>
      <button type="button" onClick={onOpen} style={S.rowMain}>
        <div style={{ ...S.circle, width: 40, height: 40, borderRadius: 20, fontSize: 13, background: pastel.bg, color: pastel.ink }}>
          <CircleLogoOrInitials url={church.logo_url} name={church.name}/>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.rowName}>{church.name}</div>
          {church.location && <div style={S.rowLoc}>{church.location}</div>}
        </div>
      </button>
      <button type="button" onClick={onToggle} style={S.followingPill}>✓ Following</button>
    </div>
  );
}

function UnfollowConfirm({ church, onCancel, onConfirm }) {
  return (
    <>
      <div className="overlay" onClick={onCancel} style={{ zIndex: 600 }}/>
      <div className="sheet" style={{ zIndex: 601, maxHeight: "none" }}>
        <div className="shandle"/>
        <div style={{ padding: "4px 20px 20px" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--ink)", marginBottom: 6 }}>Unfollow {church.name}?</div>
          <div style={{ fontSize: 13.5, color: "var(--stone)", lineHeight: 1.45, marginBottom: 16 }}>You won't see their sermons or notes unless you follow them again.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onCancel} style={{ flex: 1, background: "var(--white)", border: "1px solid var(--border-strong)", color: "var(--ink)", borderRadius: 999, padding: "11px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS }}>Cancel</button>
            <button type="button" onClick={onConfirm} style={{ flex: 1, background: "#1F1B16", border: "none", color: "#fff", borderRadius: 999, padding: "11px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: SANS }}>Unfollow</button>
          </div>
        </div>
      </div>
    </>
  );
}

const S = {
  header: { flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 18px 10px" },
  title: { fontSize: 20, fontWeight: 800, color: "var(--ink)", fontFamily: SANS },
  explainer: { fontSize: 13, color: "var(--stone)", lineHeight: 1.5, marginBottom: 16 },

  sectionHeadRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "14px 2px 8px" },
  sectionHead: { fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gold-ink)", padding: "14px 2px 8px" },
  sectionSub: { fontSize: 12, color: "var(--stone)", fontWeight: 600 },

  circle: { flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, overflow: "hidden" },

  homeCard: { display: "flex", alignItems: "center", gap: 14, background: "var(--white)", borderRadius: 18, padding: "14px 16px", marginBottom: 4 },
  homeBadge: { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--gold-ink)", display: "flex", alignItems: "center", gap: 4 },
  homeName: { fontSize: 16, fontWeight: 800, color: "var(--ink)", marginTop: 2, fontFamily: SANS },
  homeSub: { fontSize: 12, color: "var(--stone)", marginTop: 2 },
  changeLink: { flexShrink: 0, background: "none", border: "none", color: "var(--gold-ink)", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: SANS },

  card: { background: "var(--white)", borderRadius: 18, overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: 10, padding: "4px 6px" },
  rowMain: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 12, background: "none", border: "none", padding: "10px 6px", cursor: "pointer", textAlign: "left", fontFamily: SANS },
  rowName: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  rowLoc: { fontSize: 12.5, color: "var(--stone)", marginTop: 1 },

  followingPill: { flexShrink: 0, display: "flex", alignItems: "center", gap: 4, background: "var(--cream)", color: "var(--ink)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, fontFamily: SANS, cursor: "pointer", border: "none" },

  findMoreCard: { display: "flex", alignItems: "center", gap: 14, width: "100%", background: "var(--white)", border: "none", borderRadius: 18, padding: "14px 16px", marginTop: 16, cursor: "pointer", textAlign: "left", fontFamily: SANS },
  findMorePlus: { flexShrink: 0, width: 40, height: 40, borderRadius: 20, background: "#1F1B16", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700 },
  findMoreTitle: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)" },
  findMoreSub: { fontSize: 12, color: "var(--stone)", marginTop: 1 },

  footer: { textAlign: "center", fontSize: 12, color: "var(--stone)", padding: "20px 10px 6px" },
};
